// src/components/SendTemplateModal.tsx
import React, { useEffect, useState, useMemo } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  FormGroup,
  Label,
  Input,
} from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import {
  getAppRequest,
  mappingGetByInstallRequest,
  sendOperationRequest,
} from "store/integrations/action";
import { ApplicationState } from "store";
import type { InstallationSummaryDTO, MappingRecord, Metadata } from "store/integrations/types";
import { ThunkDispatch } from "@reduxjs/toolkit";
import { Action } from "redux";
import { resolveParameters, MappingRow } from "helpers/integrationHelpers";

interface SendTemplateModalProps {
  isOpen: boolean;
  toggle: () => void;
  student: { id: string; name: string; mobile: string };
  appId: string;
}

export default function SendTemplateModal({
  isOpen,
  toggle,
  student,
  appId,
}: SendTemplateModalProps) {
  const dispatch = useDispatch<ThunkDispatch<any, any, Action>>();
  const tenantId = useSelector((s: ApplicationState) => s.auth.userProfile.tenant.id!);

  // 1) Fetch metadata + installs whenever the modal opens
  const { currentApp } = useSelector((s: ApplicationState) => s.integration);
  useEffect(() => {
    if (isOpen) {
      dispatch(getAppRequest(tenantId, appId));
      // mappings come later once an install is picked
    }
  }, [dispatch, isOpen, tenantId, appId]);

  // 2) Once the user picks an install, fetch its mappings
  const [installId, setInstallId] = useState<string>("");
  useEffect(() => {
    if (installId) {
      dispatch(mappingGetByInstallRequest(tenantId, installId));
    }
  }, [dispatch, tenantId, installId]);

  // 3) Pull down installations & mappings from the store
  const installs = currentApp?.installations ?? [];
  const mappingRecords = useSelector(
    (s: ApplicationState) => s.integration.mappingRecords
  ) as MappingRecord[];

  const metadata = useMemo<Metadata | null>(
    () => {
      // getAppRequest stores metadata on currentApp.version_metadata
      // adjust to your store shape if needed
      return (currentApp?.version_metadata as Metadata) ?? null;
    },
    [currentApp]
  );

  // 4) Template‐list step & placeholder step from metadata
  const [listStep, placeholderStep] = useMemo(() => {
    if (!metadata) return [null, null];
    const list = metadata.setupSteps.find((s) =>
      s.widgets.some((w) => w.widget === "dropdown" || w.widget === "cardPicker")
    );
    const map = metadata.setupSteps.find((s) =>
      s.widgets.some((w) => w.widget === "variableMapper")
    );
    return [list || null, map || null];
  }, [metadata]);

  // 5) Once an install is chosen, we filter the mappingRecords to just that install
  const [selectedMappingId, setSelectedMappingId] = useState<string>("");

  const selectedRecord = mappingRecords.find((r) => r.id === selectedMappingId) || null;

  // 6) Build & fire the sendMessage context
  const handleSend = () => {
    if (!selectedRecord || !listStep || !placeholderStep) return;

    const cfg = selectedRecord.config as any;
    const listCfg = cfg[listStep.id];
    const placeholderCfg = cfg[placeholderStep.id];

    const templateId = listCfg.id;

    // resolve your template parameters…
    const dataSources = { students: student };
    const parameters = resolveParameters(
      placeholderCfg.mapping as MappingRow[],
      dataSources
    );

    // pull the install’s "from" number and strip any leading '+'
    const install = installs.find(i => i.id === installId)!;
    const rawSource = install.config.source as string;
    const sourceNumber = rawSource.replace(/^\+/, "");

    // likewise strip '+' from the student’s mobile
    const rawDest = student.mobile;
    const destination = rawDest.replace(/^\+/, "");

    const templateJson = JSON.stringify({ id: templateId, params: parameters });
    const context = {
      source: sourceNumber,
      template: templateJson,
      destination,
    };

    console.log("context>>>", context);

    dispatch(
      sendOperationRequest(
        tenantId,
        installId,
        "sendMessage",
        context,
        () => toggle(),
        console.error
      )
    );
  };

  const previewHtml = useMemo(() => {
    if (!selectedRecord || !placeholderStep) return "";
    const cfg: any = selectedRecord.config;
    const phCfg = cfg[placeholderStep.id] as {
      rawTemplate: { data: string };
      mapping: MappingRow[];
    };
  
    // 1) start with the raw template text
    let txt = phCfg.rawTemplate.data || "";
  
    // 2) resolve actual values
    const dataSources = { students: student };
    const params = resolveParameters(phCfg.mapping, dataSources);
  
    // 3) do the replacements
    phCfg.mapping.forEach((row, idx) => {
      const token = row.placeholder;    // e.g. "{{1}}"
      const val   = String(params[idx] || "");
      txt = txt.split(token).join(val);
    });
  
    // 4) newline → <br/>
    return txt.replace(/\n/g, "<br/>");
    
  }, [selectedRecord, placeholderStep, student]);
  

  return (
    <Modal isOpen={isOpen} toggle={toggle}>
      <ModalHeader toggle={toggle}>Send Template Message</ModalHeader>
      <ModalBody>
        {/* Step A: pick an installation */}
        <FormGroup>
          <Label for="installSelect">Choose an account</Label>
          <Input
            type="select"
            id="installSelect"
            value={installId}
            onChange={(e) => {
              setInstallId(e.target.value);
              setSelectedMappingId("");
            }}
          >
            <option value="">-- Select --</option>
            {installs.map((i: InstallationSummaryDTO) => (
              <option key={i.id} value={i.id}>
                {i.name}
              </option>
            ))}
          </Input>
        </FormGroup>

        {/* Step B: choose a saved template (mapping record) */}
        {installId && (
          <FormGroup>
            <Label for="templateSelect">Choose a Saved Template</Label>
            <Input
              type="select"
              id="templateSelect"
              value={selectedMappingId}
              onChange={(e) => setSelectedMappingId(e.target.value)}
            >
              <option value="">-- Select Setup --</option>
              {mappingRecords.map((rec) => (
                <option key={rec.id} value={rec.id}>
                  {rec.name}
                </option>
              ))}
            </Input>
          </FormGroup>
        )}

        {/* now render your previewHtml */}
        {previewHtml && (
          <>
            <h6 className="mt-3">Template Preview</h6>
            <div
              className="border rounded p-3 mb-4 bg-light"
              dangerouslySetInnerHTML={{ __html: previewHtml }}
            />
          </>
        )}
      </ModalBody>

      <ModalFooter>
        <Button
          color="primary"
          onClick={handleSend}
          disabled={!selectedRecord}
        >
          Send Message
        </Button>
        <Button color="secondary" onClick={toggle}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
}
